import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';

const EMPTY = { name: '', description: '', url: '', method: 'GET', query_param: 'q', auth_header_name: 'Authorization', auth_value: '', enabled: true };

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-foreground">{label}</span>
    {children}
    {hint && <span className="block text-xs text-muted-foreground mt-1">{hint}</span>}
  </label>
);

const Advanced = ({ f, set }) => (
  <div className="grid sm:grid-cols-3 gap-3 rounded-lg bg-muted/40 p-3" data-testid="api-connection-advanced">
    <p className="sm:col-span-3 text-xs text-muted-foreground">Only change these if your software provider told you to. The defaults work for most systems.</p>
    <Field label="Request type">
      <select value={f.method} onChange={set('method')} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="api-connection-method-select">
        <option value="GET">GET (default)</option>
        <option value="POST">POST</option>
      </select>
    </Field>
    <Field label="Search word name"><Input value={f.query_param} onChange={set('query_param')} data-testid="api-connection-param-input" /></Field>
    <Field label="Key goes in"><Input value={f.auth_header_name} onChange={set('auth_header_name')} data-testid="api-connection-header-input" /></Field>
  </div>
);

export const ConnectionForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState({ ...EMPTY, ...initial, auth_value: '' });
  const [busy, setBusy] = useState(false);
  const [adv, setAdv] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => { e.preventDefault(); setBusy(true); await onSave(f); setBusy(false); };

  return (
    <form onSubmit={submit} className="rounded-xl border border-primary/40 p-4 space-y-4" data-testid="api-connection-form">
      <Field label="1. Give it a name" hint="Anything you like, so you recognise it.">
        <Input value={f.name} onChange={set('name')} placeholder="My inventory" required data-testid="api-connection-name-input" />
      </Field>
      <Field label="2. What should your Pro check with it?" hint="In your own words. This tells your Pro when to use it.">
        <Textarea value={f.description} onChange={set('description')} rows={2} placeholder="If a product is in stock and how much it costs" required data-testid="api-connection-description-input" />
      </Field>
      <Field label="3. Paste the link from your provider" hint="Your software provider gives you this. It usually starts with https://">
        <Input value={f.url} onChange={set('url')} placeholder="https://..." required data-testid="api-connection-url-input" />
      </Field>
      <Field label="4. Paste the key from your provider" hint={initial?.auth_value_set ? 'Already saved. Leave blank to keep it.' : 'Paste it exactly as they gave it to you. Leave blank if they said no key is needed.'}>
        <Input type="password" value={f.auth_value} onChange={set('auth_value')} autoComplete="off" disabled={f.clear_auth} data-testid="api-connection-key-input" />
        {initial?.auth_value_set && (
          <span className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            <input type="checkbox" checked={!!f.clear_auth} onChange={(e) => setF({ ...f, clear_auth: e.target.checked, auth_value: '' })} data-testid="api-connection-clear-key-checkbox" /> Remove saved key
          </span>
        )}
      </Field>
      <button type="button" onClick={() => setAdv(!adv)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="api-connection-advanced-toggle">
        Advanced {adv ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {adv && <Advanced f={f} set={set} />}
      <div className="flex gap-2">
        <Button type="submit" disabled={busy} data-testid="api-connection-save-btn">{busy ? 'Saving...' : 'Save'}</Button>
        <Button type="button" variant="ghost" onClick={onCancel} data-testid="api-connection-cancel-btn">Cancel</Button>
      </div>
      <p className="text-xs text-muted-foreground">After saving, press the <span className="font-medium">play</span> button to test it with a real product name.</p>
    </form>
  );
};
